import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { type ModalKey } from "./modalConfig";
import { AppPreviewModal } from "./AppPreviewModal";

type AppModalContextValue = {
  open: (key: ModalKey) => void;
  close: () => void;
  current: ModalKey | null;
};

const AppModalContext = createContext<AppModalContextValue | null>(null);

/**
 * Holds the single active app-preview modal and renders it once at the app
 * root. Any node/chip in the tree calls useAppModal().open("drop") to launch a
 * preview — one shared Dialog instance, so only one iframe is ever live.
 */
export function AppModalProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ModalKey | null>(null);
  const open = useCallback((key: ModalKey) => setCurrent(key), []);
  const close = useCallback(() => setCurrent(null), []);

  return (
    <AppModalContext.Provider value={{ open, close, current }}>
      {children}
      <AppPreviewModal current={current} onClose={close} />
    </AppModalContext.Provider>
  );
}

export function useAppModal(): AppModalContextValue {
  const ctx = useContext(AppModalContext);
  if (!ctx) throw new Error("useAppModal must be used within <AppModalProvider>");
  return ctx;
}
